import { Button, Input, InputRef, Space, Spin, Table } from 'antd'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { fetchSOWRevenueReport, getSOWRevenueDownloadFile } from '../../store/reducers/reportsSlice';
import { formatNumberWithCommas, rejectedApiMessage } from '../../utilities/CommonHelper';
import { DownloadOutlined } from '@ant-design/icons';

import "./sowRevenueContainer.css";
import { ColumnsType } from 'antd/es/table';
import { ColumnType, FilterConfirmProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { EyeOutlined } from '@ant-design/icons';
import { SearchOutlined } from '@ant-design/icons';
import WorkOrderRevenueModal from '../WorkOrderRevenueModal/workOrderRevenueModal';


interface DataType {
    key: React.Key;
    sowId: string;
    sowName: string;
    clientEntity: string;
    sowStartDate: string;
    sowEndDate: string;
    currency: string;
    contractValue: string;
    contractValueUsd: string;
    revenueRecognizedUsd: string;
    obo: string;
}

type DataIndex = keyof DataType;

const SowRevenueContainer = () => {

    const dispatch = useDispatch();
    const reportsData = useSelector((state: any) => state.reports);
    const [searchText, setSearchText] = React.useState('');
    const [searchedColumn, setSearchedColumn] = React.useState('');
    const searchInput = React.useRef<InputRef>(null);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedSOW, setSelectedSOW] = React.useState(null);
    const [sowRevenueList, setSowRevenueList] = React.useState([]);

    useEffect(() => {
        dispatch(fetchSOWRevenueReport()).then((res: any) => {
            if (res.type === 'reports/fetchSOWRevenueReport/fulfilled') {     
            setSowRevenueList(res.payload || []);
            }else{
              rejectedApiMessage(res.payload);
            } 
          });
    }, [])

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
      ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      };
    
      const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
      };
      const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<any> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={searchInput}
              placeholder={`Search ${dataIndex}`}
              value={selectedKeys[0]}
              onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
              style={{ marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Search
              </Button>
              <Button
                onClick={() => {
                  setSelectedKeys([]);
                  handleSearch([], confirm, dataIndex);
                }}
                size="small"
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        ),
        filterIcon: (filtered: boolean) => (
          <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
        record[dataIndex] && record[dataIndex]
            .toString()
            .toLowerCase()
            .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
          if (visible) {
            setTimeout(() => searchInput.current?.select(), 100);
          }
        },
        render: (text) =>
          searchedColumn === dataIndex ? (
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={[searchText]}
              autoEscape
              textToHighlight={text ? text.toString() : ''}
            />
          ) : (
            text
          ),
      });

      const viewWorkOrderLevelRevenue = (record: any) => {
        setIsModalOpen(true);
        setSelectedSOW(record);
        console.log(record);
      }
    

    const handleReportDownload = () => {
        {
            dispatch(getSOWRevenueDownloadFile({payload:{}})).then(
              (response: any) => {
                if (response.type !== 'reports/getSOWRevenueDownloadFile/fulfilled') {
                  rejectedApiMessage(response.payload);
                }
                else{
                  
                  const fileName = response.payload?.headers?.get("content-disposition")?.split("filename=")[1];
                  const blob = response.payload.data;
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', fileName); //or any other extension
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                }
              }
            );
          }
    }

    const columns : ColumnsType<DataType> = [
        //columns for SOW Revenue Report
        {
          title: 'SOW ID',
          dataIndex: 'sowId',
          key: 'sowId',
          ...getColumnSearchProps('sowId'),
          width:150,
          fixed: 'left',
        },
        {
          title: 'SOW Name',
          dataIndex: 'sowName',
          key: 'sowName',
          ...getColumnSearchProps('sowName'),
          width:250,
        },
        {
          title: 'Client Entity',
          dataIndex: 'clientEntity',
          key: 'clientEntity',
          ...getColumnSearchProps('clientEntity'),
          width:200,
        },
        {
          title: 'SOW Start Date',
          dataIndex: 'sowStartDate',
          key: 'sowStartDate',
          ...getColumnSearchProps('sowStartDate'),
          width:150,
        },
        {
          title: 'SOW End Date',
          dataIndex: 'sowEndDate',
          key: 'sowEndDate',
          ...getColumnSearchProps('sowEndDate'),
          width:150,
        },
        // {
        //   title: 'Currency',
        //   dataIndex: 'currency',
        //   key: 'currency',
        //   ...getColumnSearchProps('currency'),
        // },
        {
          title: ' Total Contract Value',
          dataIndex: 'contractValue',
          key: 'contractValue',
          // ...getColumnSearchProps('contractValue'),
          render: (text: string) => {
            return(
              <div>{formatNumberWithCommas(text)}</div>
            )
          },
          width:150,
        },
        {
          title: 'Total Contract Value (USD)',
          dataIndex: 'contractValueUsd',
          key: 'contractValueUsd',
          // ...getColumnSearchProps('contractValueUsd'),
          render: (text: string) => {
            return(
              <div>{`$${formatNumberWithCommas(text)}`}</div>
            )
          },
          width:150,
        },
        {
          title: 'Revenue Recognized (USD)',
          dataIndex: 'revenueRecognized',
          key: 'revenueRecognized',
          // ...getColumnSearchProps('revenueRecognizedUsd'),
          render: (text: string) => {
            return(
              <div>{text?`$${formatNumberWithCommas(text)}`:'$0'}</div>
            )
          },
          width:150,
        },
        {
          title: 'OBO Value',
          dataIndex: 'obo',
          key: 'obo',
          // ...getColumnSearchProps('obo'),
          render: (text: string) => {
            return(
              <div>{`$${formatNumberWithCommas(text)}`}</div>
            )
          },
          width:150,
        },
        {
          title: 'Work Orders',
          dataIndex: 'action',
          key: 'action',
          fixed: 'right',
          width: 150,
          render: (text: string, record: any) => {
            return(
              <Button type='link' style={{background:'none', border:'none'}} 
              // disabled={record.workOrderDetails.length==0?true:false} 
              onClick={()=>viewWorkOrderLevelRevenue(record)}><EyeOutlined /></Button>
            )
          }
        },
      ];


  return (
    <Spin spinning={reportsData?.loading } tip="Loading..." >
    <div className='row'>
        <h2 className='col-lg-6 col-sm-12 page-heading'>
            SOW Revenue Report
        </h2>
        <div className='col-lg-6 col-sm-12' style={{display:'flex',justifyContent:'flex-end', padding:'20px'}}>
        <Button type="primary" style={{borderRadius:'0', backgroundColor:'#034ea2', font:'normal normal 600 12px / 16px Nunito Sans'}}
         icon={<DownloadOutlined />} 
         size={'large'}
         onClick={()=>handleReportDownload()}>
            Download
          </Button>
        </div>
        

    </div>

    <Table 
      className='table-style'
      columns={columns} 
    //   dataSource={oboReportData?oboReportData:[]  } 
    dataSource={sowRevenueList}
      scroll={{ x: 'max-content', y: 350}}
        pagination={{
          pageSize: 10,
        showSizeChanger: false,
        }}
         />
        <WorkOrderRevenueModal onCancel={()=>{
            setIsModalOpen(false);
            setSelectedSOW(null);
        }}
        selectedSOW={selectedSOW}
      isModalOpen={isModalOpen}/>
    </Spin>
  )
}

export default SowRevenueContainer;